import type {
  ConsultantRecommendationResponse,
  CosmosConversation,
  CosmosConversationMessage,
} from "./cosmos-api";
import { stripWorkspaceCommand } from "./workspace-commands";

export type ConversationMode = "query" | "recommend";

export type RestoredChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  prompt?: string;
  command?: string;
  serverMessageId?: string;
  feedback?: "helpful" | "not_helpful";
  status: "complete";
  consultantResult?: ConsultantRecommendationResponse | null;
};

const RECOMMEND_INTENT_ID = "consultant.recommend";

export const inferConversationMode = (
  conversation: Pick<CosmosConversation, "mode"> | undefined,
  messages: CosmosConversationMessage[],
): ConversationMode => {
  if (conversation?.mode === "recommend") {
    return "recommend";
  }
  if (
    messages.some(
      (entry) => entry.role === "assistant" && entry.intentId === RECOMMEND_INTENT_ID,
    )
  ) {
    return "recommend";
  }
  const firstUser = messages.find((entry) => entry.role === "user");
  if (firstUser?.content.trimStart().toLowerCase().startsWith("/recommend")) {
    return "recommend";
  }
  return "query";
};

const buildRestoredRecommendationResult = (
  message: CosmosConversationMessage,
): ConsultantRecommendationResponse | undefined => {
  if (message.role !== "assistant") {
    return undefined;
  }
  if (message.recommendationSnapshot) {
    return message.recommendationSnapshot;
  }
  if (message.intentId !== RECOMMEND_INTENT_ID) {
    return undefined;
  }
  const engine =
    message.model === "meilisearch-chat" || message.model === "catalog"
      ? message.model
      : "meilisearch-chat";
  return {
    recommendationId: message.id,
    candidates: {
      journeys: [],
      courses: [],
      lessons: [],
      modules: [],
      events: [],
      blogPosts: [],
      resources: [],
    },
    ranked: [],
    journeyBlueprint: {
      title: "Study plan",
      summary: "",
      totalEstimatedMinutes: 0,
      steps: [],
    },
    chatAnswer: message.content,
    recommendationEngine: engine,
  };
};

export const restoreConversationMessages = (
  history: CosmosConversationMessage[],
  mode: ConversationMode,
): RestoredChatMessage[] => {
  let lastUserPrompt = "";

  return history.map((entry) => {
    if (entry.role === "user") {
      lastUserPrompt = stripWorkspaceCommand(entry.content);
      const hasRecommendCommand = entry.content.trimStart().toLowerCase().startsWith("/recommend");
      return {
        id: entry.id,
        role: entry.role,
        content: entry.content,
        command: hasRecommendCommand ? "/recommend" : undefined,
        status: "complete" as const,
      };
    }

    const consultantResult =
      mode === "recommend" ? buildRestoredRecommendationResult(entry) : undefined;

    return {
      id: entry.id,
      role: entry.role,
      content: entry.content,
      prompt: lastUserPrompt || undefined,
      command: mode === "recommend" ? "/recommend" : undefined,
      serverMessageId: entry.id,
      feedback: entry.feedback,
      status: "complete" as const,
      consultantResult: consultantResult ?? null,
    };
  });
};
